<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { usePersisted } from '../use_persisted.js';
import { radian } from '../calc.js';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue', 'confirm']);

const videoEl = ref(null);
let stream = null;
let sensorTimeout = null;

// Rolling window of recent angle samples ({t, v}), averaged — a bare hand
// holding the phone up jitters by a couple of degrees, so a raw per-event
// reading is too twitchy to read. Kept short (0.25s, not a full second)
// since the sensor's own sample rate is already the dominant source of lag
// — confirmed by comparing against smoothing disabled entirely, which felt
// just as slow to react. A longer window only made that worse without
// smoothing out much more jitter.
const SMOOTHING_WINDOW_MS = 250;

// Averaging is great while the phone is held roughly still, but it makes
// deliberately re-aiming (or re-leveling) feel laggy — the reading would
// keep dragging toward wherever it used to point. So: watch for a real
// swing (a jump of more than JUMP_THRESHOLD_DEG between consecutive raw
// samples) rather than jitter, and once it's happened JUMP_STREAK_TO_RESET
// times in a row (so a single noisy sample can't trigger it), drop the
// buffer and start the average over from the new position.
const JUMP_THRESHOLD_DEG = 2;
const JUMP_STREAK_TO_RESET = 2;

// Shared by the pitch and roll readings below (both derived from the
// gravity vector, see onMotion) — same jitter and same "don't lag behind
// a deliberate move" behavior applies to both.
// Kept as a plain array internally rather than a ref: it's pushed/shifted
// on every orientation event (~60/s) and only the average needs to be
// reactive.
function makeSmoothedAngle() {
  const angle = ref(null);
  let samples = [];
  let lastRaw = null;
  let jumpStreak = 0;
  return {
    angle,
    reset() {
      samples = [];
      lastRaw = null;
      jumpStreak = 0;
      angle.value = null;
    },
    ingest(raw, now) {
      if (lastRaw != null && Math.abs(raw - lastRaw) > JUMP_THRESHOLD_DEG) {
        jumpStreak++;
        if (jumpStreak >= JUMP_STREAK_TO_RESET) samples = [];
      } else {
        jumpStreak = 0;
      }
      lastRaw = raw;
      samples.push({ t: now, v: raw });
      while (samples.length && now - samples[0].t > SMOOTHING_WINDOW_MS) samples.shift();
      angle.value = samples.reduce((sum, s) => sum + s.v, 0) / samples.length;
    },
  };
}

const status = ref('idle'); // idle | starting | ready | error
const errorMessage = ref('');
const pitch = makeSmoothedAngle(); // rolling ~0.25s average of the derived pitch angle, degrees (app's Cut convention — see onMotion)
const roll = makeSmoothedAngle(); // rolling ~0.25s average of the derived roll angle, degrees
const sensorTimedOut = ref(false);
const screenAngle = ref(0);

// Inline position/size for the overlay, tracking window.visualViewport
// rather than plain `inset: 0`. iOS auto-zooms the page when the Cut input
// gets focused (any focused control under 16px font size does this), and a
// `position: fixed` element only ever covers the LAYOUT viewport — while
// zoomed in, that's bigger than what's actually on screen, so a plain
// inset:0 overlay gets visibly cut off. The visual viewport's own
// offset/size already accounts for whatever zoom/scroll is in effect, so
// mirroring it here keeps the overlay exactly matching what's visible even
// if the zoom hasn't settled back to 1x yet by the time this opens.
const overlayStyle = ref({});

function updateOverlayRect() {
  const vv = window.visualViewport;
  if (!vv) {
    overlayStyle.value = {};
    return;
  }
  overlayStyle.value = {
    left: `${vv.offsetLeft}px`,
    top: `${vv.offsetTop}px`,
    width: `${vv.width}px`,
    height: `${vv.height}px`,
    // Override the class's `inset: 0` cleanly rather than leaning on the
    // over-constrained-box resolution rules to drop these for us.
    right: 'auto',
    bottom: 'auto',
  };
}

// Some phones/mounts end up reading backwards from what we assume below —
// rather than chase every device's quirks, let the user flip it once and
// remember the choice (see rawAngle).
const invert = usePersisted('cut-angle-camera-invert', false);

// Only trustworthy in portrait: the geometry below assumes the phone is
// held upright like a viewfinder (screen facing the user, camera facing
// the target, top of the phone up). Landscape would need a different axis
// and we'd rather say "turn the phone" than guess wrong and hand back a
// confidently incorrect angle.
const isPortrait = computed(() => screenAngle.value === 0 || screenAngle.value === 180);

// Pitch and roll are derived from the raw gravity vector
// (DeviceMotionEvent.accelerationIncludingGravity, in the device's own
// local coordinates: X right, Y toward the top of the phone, Z out of the
// screen when flat) rather than DeviceOrientationEvent's alpha/beta/gamma.
//
// This app's whole working pose — phone held upright, aiming near the
// horizon — sits almost exactly on the gimbal-lock singularity of the
// alpha/beta/gamma decomposition browsers compute internally: a pure roll
// motion at that pose mostly shows up as a swing in *beta* (the axis this
// used to read as pitch), not gamma, and gamma itself saturates toward
// ±90° almost immediately for any nonzero roll. That's not fixable with
// more trig on top of beta/gamma — by the time the browser hands them to
// JS the two are already confounded at the source. The gravity vector
// doesn't have this problem: it's a plain 3-axis reading with no Euler
// decomposition involved, so there's no singularity anywhere near this
// app's actual working range. It also drops the dependency on the
// magnetometer (alpha/compass) entirely, which was never used here but is
// usually the noisiest of the three sensor types anyway.
//
// At the reference pose (upright, level, plumb), gravity's reaction force
// reads along local +Y (see onMotion). Tilting the camera up/down (pitch)
// trades that reading between Y and Z; canting it left/right around its
// own aiming axis (roll) trades it between X and Y, independently of
// pitch. Working through the rotation gives, for the normalized gravity
// vector (gx, gy, gz):
//   pitch (this app's convention: positive = target below, downhill) = asin(gz)
//   roll = atan2(gx, gy)
// onMotion computes both per sample and feeds them through the same
// smoothing as before; this computed just applies the same invert toggle
// as always, now on a value that no longer has roll folded into its error.
const rawAngle = computed(() => {
  if (pitch.angle.value == null) return null;
  return invert.value ? -pitch.angle.value : pitch.angle.value;
});

// Purely informational — lets the user see when they've rolled the phone
// off true vertical while lining up the shot, since a canted phone makes
// the crosshair itself less trustworthy as a level reference even though
// (unlike before) it no longer biases the pitch reading above. Sign here
// hasn't been checked against a real device; if "roll left"/"roll right"
// come out backwards it's a one-line flip (negate in onMotion), not a
// re-derivation — low stakes since nothing downstream depends on it.
const rollAngle = computed(() => roll.angle.value);

const noSensor = computed(() => status.value === 'ready' && pitch.angle.value == null && sensorTimedOut.value);

function updateScreenAngle() {
  screenAngle.value = window.screen?.orientation?.angle ?? (typeof window.orientation === 'number' ? window.orientation : 0);
}

function onMotion(e) {
  const g = e.accelerationIncludingGravity;
  if (!g || g.x == null || g.y == null || g.z == null) return;

  // Guards the normalization below against a near-zero magnitude (e.g. a
  // momentary bogus reading) rather than dividing by ~0 — real gravity is
  // ~9.8 m/s^2, so anything under 1 clearly isn't a usable reading.
  const mag = Math.sqrt(g.x * g.x + g.y * g.y + g.z * g.z);
  if (mag < 1) return;

  clearTimeout(sensorTimeout);
  sensorTimedOut.value = false;

  const now = performance.now();
  // Negated: accelerationIncludingGravity apparently reports the vector
  // pointing *along* gravity's own pull here, not the reaction force
  // opposing it (the convention the derivation above assumed) — confirmed
  // on-device (pitch came out flipped, and roll read +-180 at plumb,
  // heading toward -+90 at a 90 degree roll — exactly what negating the
  // whole vector predicts, verified against the un-negated formula
  // numerically before applying this). One flip here fixes both; the
  // formulas themselves stay as derived.
  const gx = -g.x / mag;
  const gy = -g.y / mag;
  const gz = -g.z / mag;

  const pitchDeg = Math.asin(Math.max(-1, Math.min(1, gz))) / radian;
  // Negated on top of the vector flip above: pitch and the roll magnitude
  // (0 at plumb, growing correctly toward +-90) came back correct once
  // that flip was applied, but left/right came out mirrored — atan2(-y,x)
  // == -atan2(y,x) for any x,y, so this is exactly the same reading with
  // only its rotational direction reversed, isolated to roll alone.
  const rollDeg = -Math.atan2(gx, gy) / radian;

  pitch.ingest(pitchDeg, now);
  roll.ingest(rollDeg, now);
}

async function start() {
  status.value = 'starting';
  errorMessage.value = '';
  pitch.reset();
  roll.reset();
  sensorTimedOut.value = false;

  // The Cut field is almost always still focused (that's how this gets
  // opened) — blurring it dismisses the keyboard and lets iOS start
  // zooming back out, on top of the visualViewport tracking below.
  document.activeElement?.blur?.();

  if (!window.isSecureContext) {
    status.value = 'error';
    errorMessage.value = 'Camera and motion sensors need a secure (https) connection.';
    return;
  }

  updateScreenAngle();
  window.addEventListener('orientationchange', updateScreenAngle);

  updateOverlayRect();
  window.visualViewport?.addEventListener('resize', updateOverlayRect);
  window.visualViewport?.addEventListener('scroll', updateOverlayRect);

  try {
    // iOS 13+ gates motion events behind an explicit, gesture-triggered
    // permission prompt (DeviceMotionEvent's own gate, separate from
    // DeviceOrientationEvent's — this file no longer uses the latter at
    // all). Everywhere else this API doesn't exist and sensors are just
    // available.
    if (typeof DeviceMotionEvent !== 'undefined' && typeof DeviceMotionEvent.requestPermission === 'function') {
      const perm = await DeviceMotionEvent.requestPermission();
      if (perm !== 'granted') {
        throw new Error('Motion & orientation access was denied. Enable it in Settings → Safari → Motion & Orientation Access, then try again.');
      }
    }
    window.addEventListener('devicemotion', onMotion);
    sensorTimeout = setTimeout(() => {
      if (pitch.angle.value == null) sensorTimedOut.value = true;
    }, 2000);

    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("This browser doesn't support camera access.");
    }
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: 'environment' } },
      audio: false,
    });
    if (videoEl.value) {
      videoEl.value.srcObject = stream;
      await videoEl.value.play();
    }
    status.value = 'ready';
  } catch (err) {
    status.value = 'error';
    errorMessage.value = err.message || String(err);
  }
}

function stop() {
  clearTimeout(sensorTimeout);
  window.removeEventListener('devicemotion', onMotion);
  window.removeEventListener('orientationchange', updateScreenAngle);
  window.visualViewport?.removeEventListener('resize', updateOverlayRect);
  window.visualViewport?.removeEventListener('scroll', updateOverlayRect);
  if (stream) {
    stream.getTracks().forEach((t) => t.stop());
    stream = null;
  }
  if (videoEl.value) videoEl.value.srcObject = null;
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) start();
    else stop();
  }
);

onBeforeUnmount(stop);

function confirmAngle() {
  if (rawAngle.value == null) return;
  emit('confirm', Math.round(rawAngle.value * 10) / 10);
  emit('update:modelValue', false);
}

// Same readiness check as the "Use This Angle" button's :disabled — lets
// tapping anywhere on the camera view confirm the angle, since reaching for
// a small button while trying to hold the phone steady on target is
// awkward. The top/bottom controls stop this event from also bubbling up
// to them (see .stop in the template) so cancelling or flipping the sign
// doesn't also confirm.
function onScreenTap() {
  if (status.value !== 'ready' || rawAngle.value == null || !isPortrait.value) return;
  confirmAngle();
}

function cancel() {
  emit('update:modelValue', false);
}
</script>

<template>
  <Teleport to="body">
    <div v-if="modelValue" class="cut-cam-overlay" :style="overlayStyle" @click="onScreenTap">
      <video ref="videoEl" class="cut-cam-video" autoplay playsinline muted></video>

      <div v-if="rollAngle != null && status === 'ready' && isPortrait" class="cut-cam-roll" :class="{ tilted: Math.abs(rollAngle) > 0.5 }">
        {{ (rollAngle > 0 ? '+' : '') + rollAngle.toFixed(1) + '°' }}
        {{ rollAngle > 0.5 ? 'roll right' : rollAngle < -0.5 ? 'roll left' : 'plumb' }}
      </div>

      <div class="cut-cam-crosshair" aria-hidden="true">
        <div class="line horizontal"></div>
        <div class="line vertical"></div>
      </div>

      <div class="cut-cam-top">
        <button type="button" class="btn-cam-icon" @click.stop="cancel" title="Cancel">&#10005;</button>
        <button
          type="button"
          class="btn-cam-icon"
          :class="{ active: invert }"
          @click.stop="invert = !invert"
          title="Reading looks backwards? Tap to flip up/down"
        >&#8645;</button>
      </div>

      <template v-if="status === 'ready' && isPortrait">
        <div
          class="cut-cam-readout"
          :class="{ up: rawAngle < -0.3, down: rawAngle > 0.3 }"
        >
          {{ rawAngle != null ? (rawAngle > 0 ? '+' : '') + rawAngle.toFixed(1) + '°' : '—' }}
          <div class="cut-cam-readout-label">
            {{ rawAngle > 0.3 ? 'downhill' : rawAngle < -0.3 ? 'uphill' : 'level' }}
          </div>
        </div>
        <p v-if="noSensor" class="cut-cam-msg">
          No motion sensor reading yet — the angle may not update on this device.
        </p>
      </template>

      <p v-else-if="status === 'ready' && !isPortrait" class="cut-cam-msg">
        Hold your phone upright (portrait) to read the angle.
      </p>

      <p v-if="status === 'starting'" class="cut-cam-msg">Requesting camera &amp; motion access…</p>
      <p v-if="status === 'error'" class="cut-cam-msg error">{{ errorMessage }}</p>

      <div class="cut-cam-bottom">
        <button
          type="button"
          class="btn-cam-use"
          :disabled="status !== 'ready' || rawAngle == null || !isPortrait"
          @click.stop="confirmAngle"
        >
          Use This Angle
        </button>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.cut-cam-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: #000;
  overflow: hidden;
}

.cut-cam-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cut-cam-crosshair {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  z-index: 1;
}

.cut-cam-crosshair .line {
  position: absolute;
  background: rgba(255, 255, 255, 0.85);
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.9);
}

.cut-cam-crosshair .line.horizontal {
  width: 64px;
  height: 2px;
  top: -1px;
  left: -32px;
}

.cut-cam-crosshair .line.vertical {
  width: 2px;
  height: 64px;
  top: -32px;
  left: -1px;
}

.cut-cam-top {
  position: absolute;
  top: max(env(safe-area-inset-top, 0px), var(--space-md));
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  padding: 0 var(--space-md);
  z-index: 2;
}

.btn-cam-icon {
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 999px;
  width: 2.75em;
  height: 2.75em;
  font-size: 1.1rem;
  cursor: pointer;
}

.btn-cam-icon.active {
  background: var(--color-accent);
}

.cut-cam-readout {
  position: absolute;
  top: calc(50% + 56px);
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
  text-align: center;
  font-size: 2.2rem;
  font-weight: var(--font-weight-semibold);
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.9);
  z-index: 2;
  pointer-events: none;
}

.cut-cam-readout.up {
  color: #7fd4ff;
}

.cut-cam-readout.down {
  color: #ffb37f;
}

.cut-cam-readout-label {
  font-size: 0.9rem;
  font-weight: var(--font-weight-normal);
  opacity: 0.85;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.cut-cam-roll {
  position: absolute;
  top: calc(50% - 44px);
  left: 50%;
  transform: translate(-50%, -100%);
  color: #fff;
  opacity: 0.7;
  text-align: center;
  font-size: 0.9rem;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.9);
  z-index: 2;
  pointer-events: none;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.cut-cam-roll.tilted {
  color: #ffe27f;
}

.cut-cam-msg {
  position: absolute;
  bottom: 6.5em;
  left: 1em;
  right: 1em;
  color: #fff;
  text-align: center;
  background: rgba(0, 0, 0, 0.55);
  padding: var(--space-sm);
  border-radius: var(--border-radius);
  z-index: 2;
  margin: 0;
}

.cut-cam-msg.error {
  color: #ffb3b3;
}

.cut-cam-bottom {
  position: absolute;
  bottom: max(env(safe-area-inset-bottom, 0px), var(--space-lg));
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  padding: 0 var(--space-lg);
  z-index: 2;
}

.btn-cam-use {
  background: var(--color-accent);
  color: var(--color-text-light);
  border: none;
  border-radius: var(--border-radius);
  padding: var(--space-md) var(--space-xl);
  font-size: 1.1rem;
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
}

.btn-cam-use:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
